import { useEffect, useCallback, useMemo, useState } from 'react';
import { useBusinessUnitStore } from '../stores/businessUnitStore';
import { useAuthStore } from '../stores/authStore';
import { organizationApi, type AggregateParams } from '../services/api/endpoints/organization';
import type { 
  BusinessUnit, 
  BusinessUnitType,
  BusinessUnitStatus,
  OrganizationHierarchy,
  BusinessUnitOption,
  BusinessUnitFilter,
  BusinessUnitFilterDto,
  BusinessUnitAggregateDto,
  BusinessUnitHierarchyDto,
  BusinessUnitQuickStats,
  CreateBusinessUnitDto,
  UpdateBusinessUnitDto,
  PaginatedBusinessUnitsResponse
} from '../types/business-unit';

/**
 * Données mock pour le développement - DÉSACTIVÉ
 * Ces données ne doivent plus être utilisées en production
 * Note: Les IDs sont des UUID et les codes sont alphanumériques (8 caractères)
 */
const MOCK_BUSINESS_UNIT_ID = 'a1b2c3d4-e5f6-7890-abcd-ef1234567890';

/**
 * Vérifie si un ID est un ID mock (ne doit pas être utilisé pour les appels API)
 */
function isMockId(id: string): boolean {
  return id.startsWith('a1b2c3d4-') || 
         id.startsWith('b1c2d3e4-') || 
         id.startsWith('c1d2e3f4-') ||
         id.startsWith('d1e2f3a4-') ||
         id.startsWith('e1f2a3b4-') ||
         id.startsWith('f1a2b3c4-');
}

/**
 * Crée une hiérarchie vide (seulement l'entreprise, sans branches ni POS)
 * Utilisée quand l'API ne retourne pas de données ou échoue
 */
function createEmptyHierarchy(organizationName?: string): OrganizationHierarchy {
  return {
    company: {
      id: 'company-placeholder',
      code: 'COMPANY',
      name: organizationName || 'Mon Entreprise',
      type: 'company',
      status: 'active',
      companyId: 'company-placeholder',
      hierarchyLevel: 0,
      country: 'RDC',
      currency: 'CDF',
      timezone: 'Africa/Kinshasa',
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    },
    branches: [],
    pointsOfSale: []
  };
}

/**
 * Hook pour gérer le contexte d'unité commerciale
 * Fournit des fonctions utilitaires pour filtrer les données par niveau organisationnel
 * 
 * Intégration avec authStore:
 * - Initialise le scope utilisateur depuis authStore
 * - Respecte les permissions de l'utilisateur (company vs unit)
 */
export function useBusinessUnit() {
  // Récupérer l'organisation depuis authStore (source de vérité)
  const { organization, isAuthenticated } = useAuthStore();
  
  const {
    hierarchy,
    selectedUnit,
    filterContext,
    unitOptions,
    loading,
    includeChildren,
    userScope,
    userBusinessUnit,
    setHierarchy,
    selectUnit,
    selectUnitById,
    setIncludeChildren,
    resetToCompany,
    setLoading,
    getSelectedUnitIds,
    isCompanyLevel,
    isBranchLevel,
    isPosLevel,
    initializeFromAuth,
    canSelectUnit
  } = useBusinessUnitStore();
  
  // Initialiser le store depuis authStore au montage
  useEffect(() => {
    if (isAuthenticated) {
      initializeFromAuth();
    }
  }, [isAuthenticated, initializeFromAuth]);
  
  /**
   * Charge la hiérarchie organisationnelle depuis l'API
   * Avec fallback sur les données mock en cas d'erreur
   */
  const loadHierarchy = useCallback(async () => {
    setLoading(true);
    try {
      // Appeler l'API réelle pour charger la hiérarchie
      // Passer le nom et l'ID de l'organisation pour enrichir les données
      const response = await organizationApi.getHierarchy(
        organization?.name,
        organization?.id
      );
      
      if (response.success && response.data) {
        // Utiliser les données de l'API
        const hierarchyWithOrgName: OrganizationHierarchy = {
          ...response.data,
          company: {
            ...response.data.company,
            name: organization?.name || response.data.company.name,
            id: organization?.id || response.data.company.id
          }
        };
        setHierarchy(hierarchyWithOrgName);
      } else {
        // Pas de données de l'API - utiliser une hiérarchie vide (sans mock)
        console.warn('API hiérarchie indisponible, utilisation d\'une hiérarchie vide');
        setHierarchy(createEmptyHierarchy(organization?.name));
      }
    } catch (error) {
      console.error('Erreur lors du chargement de la hiérarchie:', error);
      // En cas d'erreur - utiliser une hiérarchie vide (sans mock)
      setHierarchy(createEmptyHierarchy(organization?.name));
    } finally {
      setLoading(false);
    }
  }, [organization?.name, organization?.id, setHierarchy, setLoading]);
  
  // Charger la hiérarchie au montage ET quand l'organisation change (switch entreprise auditeur)
  useEffect(() => {
    if (organization?.id) {
      loadHierarchy();
    }
  }, [organization?.id]); // Re-exécuter quand l'organisation change
  
  /**
   * Crée un objet de filtre pour l'unité commerciale sélectionnée
   */
  const getBusinessUnitFilter = useCallback((): BusinessUnitFilter => {
    if (!selectedUnit) {
      return {};
    }
    
    return {
      businessUnitId: selectedUnit.id,
      businessUnitType: selectedUnit.type,
      includeChildren
    };
  }, [selectedUnit, includeChildren]);
  
  /**
   * Vérifie si une entrée/transaction appartient à l'unité sélectionnée
   */
  const belongsToSelectedUnit = useCallback((itemBusinessUnitId?: string): boolean => {
    if (!selectedUnit || !itemBusinessUnitId) {
      return true; // Si pas de filtre ou pas d'ID, on inclut tout
    }
    
    const selectedIds = getSelectedUnitIds();
    return selectedIds.includes(itemBusinessUnitId);
  }, [selectedUnit, getSelectedUnitIds]);
  
  /**
   * Retourne le label du niveau actuel
   */
  const currentLevelLabel = useMemo(() => {
    if (!selectedUnit) return 'Tous';
    
    const labels: Record<BusinessUnitType, string> = {
      company: 'Entreprise',
      branch: 'Succursale',
      pos: 'Point de vente'
    };
    
    return labels[selectedUnit.type];
  }, [selectedUnit]);
  
  /**
   * Retourne les options de succursales disponibles
   */
  const branchOptions = useMemo(() => {
    return unitOptions.filter(opt => opt.type === 'branch');
  }, [unitOptions]);
  
  /**
   * Retourne les options de points de vente pour une succursale donnée
   */
  const getPosOptionsForBranch = useCallback((branchId: string): BusinessUnitOption[] => {
    if (!hierarchy) return [];
    
    return unitOptions.filter(
      opt => opt.type === 'pos' && 
      hierarchy.pointsOfSale.find(pos => pos.id === opt.id)?.branchId === branchId
    );
  }, [hierarchy, unitOptions]);
  
  /**
   * Retourne le chemin complet de l'unité sélectionnée
   */
  const selectedUnitPath = useMemo(() => {
    if (!selectedUnit) return '';
    
    const option = unitOptions.find(opt => opt.id === selectedUnit.id);
    return option?.fullPath || selectedUnit.name;
  }, [selectedUnit, unitOptions]);
  
  /**
   * Sélectionne rapidement le niveau entreprise
   */
  const selectCompanyLevel = useCallback(() => {
    resetToCompany();
  }, [resetToCompany]);
  
  /**
   * Sélectionne rapidement une succursale par son ID
   */
  const selectBranch = useCallback((branchId: string) => {
    selectUnitById(branchId);
  }, [selectUnitById]);
  
  /**
   * Sélectionne rapidement un point de vente par son ID
   */
  const selectPos = useCallback((posId: string) => {
    selectUnitById(posId);
  }, [selectUnitById]);

  // ============================================
  // NOUVELLES FONCTIONS API (Alignées avec la doc)
  // ============================================

  /**
   * Créer une nouvelle Business Unit
   * POST /business-units
   */
  const createBusinessUnit = useCallback(async (data: CreateBusinessUnitDto): Promise<BusinessUnit | null> => {
    try {
      setLoading(true);
      const response = await organizationApi.createBusinessUnit(data);
      if (response.success && response.data) {
        // Recharger la hiérarchie pour refléter les changements
        await loadHierarchy();
        return response.data;
      }
      console.error('Échec de la création:', response.error);
      return null;
    } catch (error) {
      console.error('Erreur lors de la création de la Business Unit:', error);
      return null;
    } finally {
      setLoading(false);
    }
  }, [loadHierarchy, setLoading]);

  /**
   * Lister les Business Units avec filtres et pagination
   * GET /business-units
   */
  const fetchBusinessUnits = useCallback(async (params?: BusinessUnitFilterDto): Promise<PaginatedBusinessUnitsResponse | null> => {
    try {
      const response = await organizationApi.getBusinessUnits(params);
      if (response.success && response.data) {
        return response.data;
      }
      console.error('Échec de la récupération:', response.error);
      return null;
    } catch (error) {
      console.error('Erreur lors de la récupération des Business Units:', error);
      return null;
    }
  }, []);

  /**
   * Obtenir la hiérarchie complète (format DTO)
   * GET /business-units/hierarchy
   */
  const fetchHierarchyDto = useCallback(async (): Promise<BusinessUnitHierarchyDto | null> => {
    try {
      const response = await organizationApi.getBusinessUnitsHierarchy();
      if (response.success && response.data) {
        return response.data;
      }
      return null;
    } catch (error) {
      console.error('Erreur lors de la récupération de la hiérarchie:', error);
      return null;
    }
  }, []);

  /**
   * Obtenir une Business Unit par son ID
   * GET /business-units/:id
   */
  const fetchBusinessUnitById = useCallback(async (id: string): Promise<BusinessUnit | null> => {
    try {
      const response = await organizationApi.getBusinessUnitById(id);
      if (response.success && response.data) {
        return response.data;
      }
      return null;
    } catch (error) {
      console.error('Erreur lors de la récupération de la Business Unit:', error);
      return null;
    }
  }, []);

  /**
   * Obtenir les enfants directs d'une Business Unit
   * GET /business-units/:id/children
   */
  const fetchChildren = useCallback(async (id: string): Promise<BusinessUnit[] | null> => {
    try {
      const response = await organizationApi.getBusinessUnitChildren(id);
      if (response.success && response.data) {
        return response.data;
      }
      return null;
    } catch (error) {
      console.error('Erreur lors de la récupération des enfants:', error);
      return null;
    }
  }, []);

  /**
   * Obtenir tous les descendants d'une Business Unit
   * GET /business-units/:id/descendants
   */
  const fetchDescendants = useCallback(async (id: string): Promise<BusinessUnit[] | null> => {
    try {
      const response = await organizationApi.getBusinessUnitDescendants(id);
      if (response.success && response.data) {
        return response.data;
      }
      return null;
    } catch (error) {
      console.error('Erreur lors de la récupération des descendants:', error);
      return null;
    }
  }, []);

  /**
   * Obtenir les données agrégées d'une Business Unit
   * GET /business-units/:id/aggregate
   */
  const fetchAggregate = useCallback(async (id: string, params: AggregateParams): Promise<BusinessUnitAggregateDto | null> => {
    // Ne pas appeler l'API avec des IDs mock
    if (isMockId(id)) {
      console.warn('⚠️ Tentative d\'appel API avec un ID mock ignorée:', id);
      return null;
    }
    
    try {
      const response = await organizationApi.getBusinessUnitAggregate(id, params);
      if (response.success && response.data) {
        return response.data;
      }
      return null;
    } catch (error) {
      console.error('Erreur lors de la récupération des données agrégées:', error);
      return null;
    }
  }, []);

  /**
   * Obtenir les statistiques rapides d'une Business Unit
   * GET /business-units/:id/stats
   */
  const fetchStats = useCallback(async (id: string): Promise<BusinessUnitQuickStats | null> => {
    // Ne pas appeler l'API avec des IDs mock
    if (isMockId(id)) {
      console.warn('⚠️ Tentative d\'appel API stats avec un ID mock ignorée:', id);
      return null;
    }
    
    try {
      const response = await organizationApi.getBusinessUnitStats(id);
      if (response.success && response.data) {
        return response.data;
      }
      return null;
    } catch (error) {
      console.error('Erreur lors de la récupération des statistiques:', error);
      return null;
    }
  }, []);

  /**
   * Mettre à jour une Business Unit
   * PUT /business-units/:id
   */
  const updateBusinessUnit = useCallback(async (id: string, data: UpdateBusinessUnitDto): Promise<BusinessUnit | null> => {
    try {
      setLoading(true);
      const response = await organizationApi.updateBusinessUnit(id, data);
      if (response.success && response.data) {
        await loadHierarchy();
        return response.data;
      }
      console.error('Échec de la mise à jour:', response.error);
      return null;
    } catch (error) {
      console.error('Erreur lors de la mise à jour de la Business Unit:', error);
      return null;
    } finally {
      setLoading(false);
    }
  }, [loadHierarchy, setLoading]);

  /**
   * Désactiver une Business Unit et tous ses descendants
   * POST /business-units/:id/deactivate
   */
  const deactivateBusinessUnit = useCallback(async (id: string): Promise<BusinessUnit | null> => {
    try {
      setLoading(true);
      const response = await organizationApi.deactivateBusinessUnit(id);
      if (response.success && response.data) {
        await loadHierarchy();
        return response.data;
      }
      console.error('Échec de la désactivation:', response.error);
      return null;
    } catch (error) {
      console.error('Erreur lors de la désactivation de la Business Unit:', error);
      return null;
    } finally {
      setLoading(false);
    }
  }, [loadHierarchy, setLoading]);

  /**
   * Supprimer définitivement une Business Unit
   * DELETE /business-units/:id
   */
  const deleteBusinessUnit = useCallback(async (id: string): Promise<boolean> => {
    try {
      setLoading(true);
      const response = await organizationApi.deleteBusinessUnit(id);
      if (response.success) {
        await loadHierarchy();
        return true;
      }
      console.error('Échec de la suppression:', response.error);
      return false;
    } catch (error) {
      console.error('Erreur lors de la suppression de la Business Unit:', error);
      return false;
    } finally {
      setLoading(false);
    }
  }, [loadHierarchy, setLoading]);
  
  return {
    // État
    hierarchy,
    selectedUnit,
    filterContext,
    unitOptions,
    loading,
    includeChildren,
    
    // Scope utilisateur (depuis authStore)
    userScope,
    userBusinessUnit,
    hasCompanyAccess: userScope === 'company',
    
    // Actions de base
    loadHierarchy,
    selectUnit,
    selectUnitById,
    selectCompanyLevel,
    selectBranch,
    selectPos,
    setIncludeChildren,
    resetToCompany,
    
    // Vérification des droits d'accès
    canSelectUnit,
    
    // Utilitaires de filtrage
    getBusinessUnitFilter,
    getSelectedUnitIds,
    belongsToSelectedUnit,
    
    // Vérifications de niveau
    isCompanyLevel,
    isBranchLevel,
    isPosLevel,
    
    // Labels et options
    currentLevelLabel,
    selectedUnitPath,
    branchOptions,
    getPosOptionsForBranch,

    // ============================================
    // Nouvelles fonctions API (alignées avec la doc)
    // ============================================
    
    // CRUD Business Units
    createBusinessUnit,
    updateBusinessUnit,
    deactivateBusinessUnit,
    deleteBusinessUnit,
    
    // Récupération de données
    fetchBusinessUnits,
    fetchBusinessUnitById,
    fetchHierarchyDto,
    fetchChildren,
    fetchDescendants,
    fetchAggregate,
    fetchStats
  };
}

/**
 * Hook pour étendre les filtres existants avec le filtre d'unité commerciale
 */
export function useBusinessUnitFilterExtension<T extends object>(
  baseFilters: T
): T & BusinessUnitFilter {
  const { getBusinessUnitFilter } = useBusinessUnit();
  
  return useMemo(() => ({
    ...baseFilters,
    ...getBusinessUnitFilter()
  }), [baseFilters, getBusinessUnitFilter]);
}
